import React, { useState, useEffect } from "react";
import { useNavigate, Link, NavLink } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { TrendingUp, TrendingDown, Package, Truck, Users, ArrowUp, ArrowDown, UserCheck, UserPlus, Activity, DollarSign } from "lucide-react";
import "../style/DashboardHome.css";

const DashboardHome = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch Dashboard Data
  const fetchDashboardData = async () => {
    try {
      const res = await fetch("https://ctmapi.zerlak.com/api/v1/challans/getDashboardData", {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      const data = await res.json();
      setDashboardData(data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
      setLoading(false);
    }
  };

  // Fetch Settings Data
  const fetchSettings = async () => {
    try {
      const res = await fetch("/api/v1/settings/getSettings");
      const data = await res.json();
      setSettings(data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
    fetchSettings();
  }, []);

  if (loading) {
    return <div className="dashboard-loading">Loading...</div>;
  }

  return (
    <div className="dashboard-container">
      {/* Header */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">
            {settings?.companyName ? `${settings.companyName} Dashboard` : "Dashboard"}
          </h1>
          <p className="dashboard-subtitle">
            Total Inventory Overview
          </p>
        </div>
        <div className="header-date">
          {new Date().toLocaleDateString('en-US', { 
            weekday: 'short', 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
          })}
        </div>
      </div>

      {/* KPI Cards Section */}
      <div className="kpi-section">
        <div className="kpi-grid">
          <div className="kpi-card success" onClick={() => navigate('/dashboard/challan/delivered')} style={{ cursor: 'pointer' }}>
            <div className="kpi-icon">
              <Truck size={24} />
            </div>
            <div className="kpi-content">
              <h3>Total Delivered</h3>
              <p className="kpi-number">{dashboardData?.totalDeliveredChallans || 0}</p>
              <span className="kpi-trend positive">
                <ArrowUp size={16} />
                +12%
              </span>
            </div>
          </div>
          <div className="kpi-card warning" onClick={() => navigate('/dashboard/challan/received')} style={{ cursor: 'pointer' }}>
            <div className="kpi-icon">
              <Package size={24} />
            </div>
            <div className="kpi-content">
              <h3>Total Received</h3>
              <p className="kpi-number">{dashboardData?.totalReceivedChallans || 0}</p>
              <span className="kpi-trend positive">
                <ArrowUp size={16} />
                +8%
              </span>
            </div>
          </div>
          <div className="kpi-card info" onClick={() => navigate('/dashboard/customers/view')} style={{ cursor: 'pointer' }}>
            <div className="kpi-icon">
              <Users size={24} />
            </div>
            <div className="kpi-content">
              <h3>Total Customers</h3>
              <p className="kpi-number">{dashboardData?.totalCustomers || 0}</p>
              <span className="kpi-trend positive">
                <ArrowUp size={16} />
                +15%
              </span>
            </div>
          </div>
          <div className="kpi-card primary">
            <div className="kpi-icon">
              <TrendingUp size={24} />
            </div>
            <div className="kpi-content">
              <h3>Weekly Growth</h3>
              <p className="kpi-number">+18%</p>
              <span className="kpi-trend positive">
                <ArrowUp size={16} />
                Strong
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Charts Section */}
      <div className="charts-section">
        <div className="chart-container">
          <h2 className="section-title">Product Overview</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={dashboardData?.productSummaries?.map(p => ({
              name: p.productName,
              delivered: p.totalDelivered || 0,
              received: p.totalReceived || 0
            })) || []}>
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="delivered" fill="#3b82f6" />
              <Bar dataKey="received" fill="#10b981" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Products Section */}
      <div className="top-products-section">
        <h2 className="section-title">Top Products</h2>
        <div className="top-products-list">
          {dashboardData?.productSummaries?.slice(0, 5).map((product, idx) => (
            <div key={idx} className="top-product-item">
              <div className="product-rank">#{idx + 1}</div>
              <div className="product-info">
                <h4>{product.productName}</h4>
                <p>Delivered: {product.totalDelivered || 0}</p>
              </div>
              <div className="product-stats">
                <span className="delivered-count">{product.totalDelivered || 0}</span>
                <span className="received-count">{product.totalReceived || 0}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Customer Summary Section */}
      <div className="customer-summary-section">
        <h2 className="section-title">Customer Summary</h2>
        <div className="summary-grid">
          <div className="summary-card" onClick={() => navigate('/dashboard/customers/view')} style={{ cursor: 'pointer' }}>
            <div className="summary-icon">
              <UserCheck size={24} />
            </div>
            <div className="summary-content">
              <h4>Active Customers</h4>
              <p className="summary-number">{dashboardData?.totalCustomers || 0}</p>
              <span className="summary-trend positive">
                <ArrowUp size={14} />
                +15% this month
              </span>
            </div>
          </div>
          <div className="summary-card" onClick={() => navigate('/dashboard/customers/create')} style={{ cursor: 'pointer' }}>
            <div className="summary-icon">
              <UserPlus size={24} />
            </div>
            <div className="summary-content">
              <h4>New Customers</h4>
              <p className="summary-number">2</p>
              <span className="summary-trend positive">
                <ArrowUp size={14} />
                +2 this week
              </span>
            </div>
          </div>
          <div className="summary-card" onClick={() => navigate('/dashboard/view-challan')} style={{ cursor: 'pointer' }}>
            <div className="summary-icon">
              <Activity size={24} />
            </div>
            <div className="summary-content">
              <h4>Total Transactions</h4>
              <p className="summary-number">{(dashboardData?.totalDeliveredChallans || 0) + (dashboardData?.totalReceivedChallans || 0)}</p>
              <span className="summary-trend positive">
                <ArrowUp size={14} />
                +18% growth
              </span>
            </div>
          </div>
          <div className="summary-card" onClick={() => navigate('/dashboard/customers/view')} style={{ cursor: 'pointer' }}>
            <div className="summary-icon">
              <DollarSign size={24} />
            </div>
            <div className="summary-content">
              <h4>Avg Order Value</h4>
              <p className="summary-number">$1,250</p>
              <span className="summary-trend positive">
                <ArrowUp size={14} />
                +5% increase
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Users Activity Section */}
      <div className="users-activity-section">
        <h2 className="section-title">Users Activity</h2>
        <div className="activity-grid">
          <div className="activity-card">
            <div className="activity-header">
              <h4>Today's Activity</h4>
              <span className="activity-badge high">High</span>
            </div>
            <div className="activity-stats">
              <div className="activity-stat">
                <span className="stat-label">Deliveries</span>
                <span className="stat-value">3</span>
              </div>
              <div className="activity-stat">
                <span className="stat-label">Receivings</span>
                <span className="stat-value">1</span>
              </div>
              <div className="activity-stat">
                <span className="stat-label">Customers</span>
                <span className="stat-value">2</span>
              </div>
            </div>
          </div>
          <div className="activity-card">
            <div className="activity-header">
              <h4>This Week</h4>
              <span className="activity-badge medium">Medium</span>
            </div>
            <div className="activity-stats">
              <div className="activity-stat">
                <span className="stat-label">Deliveries</span>
                <span className="stat-value">{dashboardData?.totalDeliveredChallans || 0}</span>
              </div>
              <div className="activity-stat">
                <span className="stat-label">Receivings</span>
                <span className="stat-value">{dashboardData?.totalReceivedChallans || 0}</span>
              </div>
              <div className="activity-stat">
                <span className="stat-label">Customers</span>
                <span className="stat-value">{dashboardData?.totalCustomers || 0}</span>
              </div>
            </div>
          </div>
          <div className="activity-card">
            <div className="activity-header">
              <h4>System Health</h4>
              <span className="activity-badge good">Good</span>
            </div>
            <div className="activity-stats">
              <div className="activity-stat">
                <span className="stat-label">Uptime</span>
                <span className="stat-value">99.9%</span>
              </div>
              <div className="activity-stat">
                <span className="stat-label">Response</span>
                <span className="stat-value">12ms</span>
              </div>
              <div className="activity-stat">
                <span className="stat-label">Errors</span>
                <span className="stat-value">0</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardHome;